import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { DocumentCheckIcon, DocumentIcon } from "@heroicons/react/24/outline";

import { Box, Tooltip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { H1, PageableList, ProgressBar, TextLikeLink } from "@components";
import { workspacesService } from "features/workspaces/services/workspaces-service";
import { useBreadcrumbs } from "@hooks/useBreadcrumbs/useBreadcrumbs";
import { WORKSPACES_PATH } from "pages/router";
import type { IProjectId } from "features/projects/types";
import type { IWorkspaceId, IFileResponse } from "features/workspaces/types";
import type { GridRenderCellParams } from "@mui/x-data-grid";

export const FilesPage = () => {
  // const navigate = useNavigate();
  const { setBreadcrumbsMap } = useBreadcrumbs();
  const [title, setTitle] = useState<string>(" ");
  const [urlParams, setUrlParams] = useState({
    workspaceId: "",
    workspaceLabel: "",
    projectId: "",
    projectLabel: "",
    languageId: "",
    languageLabel: "",
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { workspaceId, projectId, languageId } = useParams() as unknown as {
    workspaceId: IWorkspaceId;
    projectId: IProjectId;
    languageId: number;
  };

  const updateBreadcrumbs = (data: any) => {
    if (!data) {
      return;
    }

    setTitle(`${data.project.name} - ${data.language.name} files:`);
    setUrlParams({
      workspaceId: data.workspace.id,
      workspaceLabel: data.workspace.name,
      projectId: data.project.id,
      projectLabel: data.project.name,
      languageId: data.language.id,
      languageLabel: data.language.name,
    });

    setBreadcrumbsMap({
      workspaceId: data.workspace.name,
      projectId: data.project.name,
      languageId: data.language.name,
      isPopulated: true,
    });
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "status",
        headerName: "",
        type: "string",
        width: 30,
        align: "center",
        renderCell: (params: GridRenderCellParams) => {
          const isDone = params.row.phrasesCount === params.row.approvedPhrasesCount;

          return isDone ? (
            <Tooltip title="All strings are approved.">
              <DocumentCheckIcon width={15} height={15} className="status--success" />
            </Tooltip>
          ) : (
            <Tooltip title="In progress">
              <DocumentIcon width={15} height={15} className="status--in-progress" />
            </Tooltip>
          );
        },
        sortable: false,
      },

      {
        field: "name",
        headerName: "File name",
        flex: 1,
        renderCell: (params: GridRenderCellParams) => (
          <>
            <TextLikeLink>{params.row.name}</TextLikeLink>
            <a
              className="row-as-link"
              href={`#/${WORKSPACES_PATH}/${urlParams.workspaceId}/${urlParams.workspaceLabel}/${urlParams.projectId}/${urlParams.projectLabel}/${urlParams.languageId}/${urlParams.languageLabel}/${params.row.id}/${params.row.name}`}
              aria-label={params.row.id}
            >
              &nbsp;
            </a>
          </>
        ),
      },
      {
        field: "completenessPercents",
        headerName: "Progress",
        flex: 1,
        renderCell: (params: GridRenderCellParams) => <ProgressBar percentage={params.formattedValue} />,
      },
      {
        flex: 1,
        field: "phrasesCount",
        headerName: "Approved / Total phrases",
        valueGetter: (rawValue: any, row: IFileResponse) => {
          const { phrasesCount, approvedPhrasesCount } = row;
          return `${approvedPhrasesCount} / ${phrasesCount}`;
        },
      },
    ],
    [workspaceId, urlParams, projectId, languageId],
  );

  // const onRowClick = (params: any) => {
  //   const to = `/${WORKSPACES_PATH}/${urlParams.workspaceId}/${urlParams.workspaceLabel}/${urlParams.projectId}/${urlParams.projectLabel}/${urlParams.languageId}/${urlParams.languageLabel}/${params.row.id}/${params.row.name}`;

  //   navigate(to);
  // };

  return (
    <>
      <H1 label={title} />

      <Box>
        <PageableList
          columns={columns}
          isRowLink
          // onRowClick={onRowClick}
          getService={workspacesService.getFiles}
          storageId={`file${workspaceId}${projectId}${languageId}`}
          disableColumnSorting={false}
          initialRequestParams={{
            projectLanguageId: languageId,
            orderBy: "name",
            descendingOrder: false,
          }}
          hasSearch
          updateBreadcrumbs={updateBreadcrumbs}
        />
      </Box>
    </>
  );
};
